/**
 * Roster supply — people become the source of pools (PRD Part A, Phase A).
 *
 * The one-sentence diagnosis this closes: the system already knows *who* can do
 * *what* at *what level* and for *how many hours a week* — and then plans
 * against an anonymous pool as if it didn't.
 *
 * Derivation, not replacement. A scenario in roster mode rolls up its active
 * role assignments into per-type pools; manual rows keep working untouched, and
 * a scenario in manual mode gets byte-identical behaviour to today.
 *
 * Pure: no React, no Supabase.
 */

import type { CapacityModelRow, Scenario } from './planningEngineModule.types';

// ---------------------------------------------------------------------------
// Domain
// ---------------------------------------------------------------------------

export type PlanningResourceKind = 'person' | 'generic' | 'external';

export const RESOURCE_KIND_LABELS: Record<PlanningResourceKind, string> = {
  person: 'Person',
  generic: 'Generic role',
  external: 'External',
};

export interface PlanningResource {
  id: string;
  workspaceId: string;
  /** Null for generic placeholders and externals — a resource need not be an app user. */
  userId: string | null;
  displayName: string;
  kind: PlanningResourceKind;
  active: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PlanningRoleAssignment {
  id: string;
  workspaceId: string;
  resourceId: string;
  resourceType: string;
  hoursPerWeek: number;
  orgUnitId: string | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  /**
   * Reserved capacity (Phase C). Dedicated hours are invisible to general
   * demand and only satisfy the named priority.
   */
  dedicatedToPriorityId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/** One person's (or placeholder's) contribution to a derived pool. */
export interface RosterContribution {
  assignmentId: string;
  resourceId: string;
  displayName: string;
  kind: PlanningResourceKind;
  hoursPerWeek: number;
  /** Weeks of the scenario horizon this assignment is actually available for. */
  effectiveWeeks: number;
  /** hoursPerWeek × effectiveWeeks, before the scenario's capacity buffer. */
  grossHours: number;
  dedicatedToPriorityId: string | null;
}

// ---------------------------------------------------------------------------
// Effective windows
// ---------------------------------------------------------------------------

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function parseDate(value: string | null | undefined): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * The window a scenario plans over.
 *
 * Prefers the explicit time-phased horizon; otherwise the planning-weeks window
 * starting `today`. `today` is a parameter so this stays pure and testable.
 */
export function scenarioHorizon(
  scenario: Scenario,
  today: Date,
): { startMs: number; endMs: number; weeks: number } {
  const explicitStart = parseDate(scenario.horizonStartDate);
  const explicitEnd = parseDate(scenario.horizonEndDate);
  if (explicitStart != null && explicitEnd != null && explicitEnd > explicitStart) {
    return {
      startMs: explicitStart,
      endMs: explicitEnd,
      weeks: (explicitEnd - explicitStart) / MS_PER_DAY / 7,
    };
  }
  const startMs = today.getTime();
  const weeks = Math.max(0, scenario.planningWeeks);
  return { startMs, endMs: startMs + weeks * 7 * MS_PER_DAY, weeks };
}

/**
 * How many weeks of the horizon an assignment is actually available for.
 *
 * Pro-rated rather than all-or-nothing: someone who joins in week 11 of a
 * 12-week horizon brings one week of capacity, not twelve. The assignment's
 * stated weekly rate is never distorted to express this — the fraction lands in
 * the derived hours, and the rate stays truthful in the drill-through.
 */
export function effectiveWeeksInHorizon(
  assignment: Pick<PlanningRoleAssignment, 'effectiveFrom' | 'effectiveTo'>,
  horizon: { startMs: number; endMs: number; weeks: number },
): number {
  if (horizon.weeks <= 0) return 0;
  const from = parseDate(assignment.effectiveFrom) ?? horizon.startMs;
  const to = parseDate(assignment.effectiveTo) ?? horizon.endMs;
  const overlapStart = Math.max(from, horizon.startMs);
  const overlapEnd = Math.min(to, horizon.endMs);
  if (overlapEnd <= overlapStart) return 0;
  return (overlapEnd - overlapStart) / MS_PER_DAY / 7;
}

// ---------------------------------------------------------------------------
// Roll-up
// ---------------------------------------------------------------------------

export interface DerivedRosterPool extends CapacityModelRow {
  source: 'roster';
  contributors: RosterContribution[];
}

/**
 * Rolls active role assignments up into one derived pool per resource type.
 *
 * The result is an ordinary `CapacityModelRow`, so the engine consumes it with
 * no changes at all — which is the whole point of derivation over replacement.
 * `weeklyCapacityHours` reports the true combined weekly rate; the derived hours
 * carry the pro-rating and the scenario buffer.
 *
 * Dedicated assignments (Phase C) are excluded from the general pool: reserved
 * hours are invisible to general demand and only satisfy their named priority.
 */
export function deriveRosterCapacityRows(args: {
  scenario: Scenario;
  resources: PlanningResource[];
  assignments: PlanningRoleAssignment[];
  /** Injected so the derivation is deterministic and testable. */
  today?: Date;
  /**
   * Keep reserved (dedicated) assignments in the contributor list.
   *
   * Only the qualified-supply builder sets this: the ledger enforces dedication
   * itself, per item, and needs the reserved units present to do so. Pool
   * TOTALS from such a call over-report general supply and must not be used as
   * capacity rows — which is why the default is off.
   */
  includeDedicated?: boolean;
}): DerivedRosterPool[] {
  const { scenario, resources, assignments, today = new Date(), includeDedicated = false } = args;
  const horizon = scenarioHorizon(scenario, today);
  const resourceById = new Map(resources.map((r) => [r.id, r]));

  const byType = new Map<string, RosterContribution[]>();

  for (const assignment of assignments) {
    const resource = resourceById.get(assignment.resourceId);
    if (!resource || !resource.active) continue;
    if (assignment.dedicatedToPriorityId && !includeDedicated) continue;
    if (assignment.hoursPerWeek <= 0) continue;

    const effectiveWeeks = effectiveWeeksInHorizon(assignment, horizon);
    if (effectiveWeeks <= 0) continue;

    const list = byType.get(assignment.resourceType) ?? [];
    list.push({
      assignmentId: assignment.id,
      resourceId: resource.id,
      displayName: resource.displayName,
      kind: resource.kind,
      hoursPerWeek: assignment.hoursPerWeek,
      effectiveWeeks,
      grossHours: assignment.hoursPerWeek * effectiveWeeks,
      dedicatedToPriorityId: assignment.dedicatedToPriorityId,
    });
    byType.set(assignment.resourceType, list);
  }

  const buffered = 1 - scenario.capacityBufferPercent;
  const now = new Date().toISOString();

  return Array.from(byType.entries())
    .map(([resourceType, contributors]) => {
      const grossHours = contributors.reduce((sum, c) => sum + c.grossHours, 0);
      const effectiveCapacityHours = grossHours * buffered;
      // The true combined weekly rate across everyone in this pool. Distinct
      // from grossHours / planningWeeks, which would understate the rate of a
      // person who is only present for part of the horizon.
      const weeklyCapacityHours = contributors.reduce((sum, c) => sum + c.hoursPerWeek, 0);
      return {
        // Deterministic synthetic id: derived rows are not stored, but the list
        // views and React keys need a stable identity across re-renders.
        id: `roster:${scenario.id}:${resourceType}`,
        projectId: scenario.projectId,
        scenarioId: scenario.id,
        resourceType,
        resourceName: describePool(contributors),
        weeklyCapacityHours,
        notes: null,
        createdAt: now,
        updatedAt: now,
        effectiveCapacityHours,
        workUnitCapacity:
          scenario.defaultWorkUnitHours > 0
            ? effectiveCapacityHours / scenario.defaultWorkUnitHours
            : 0,
        source: 'roster' as const,
        contributors: [...contributors].sort((a, b) => b.grossHours - a.grossHours),
      };
    })
    .sort((a, b) => a.resourceType.localeCompare(b.resourceType));
}

/** "4 people · 1 generic" — what the number is made of, at a glance. */
export function describePool(contributors: RosterContribution[]): string {
  const counts = new Map<PlanningResourceKind, number>();
  for (const c of contributors) counts.set(c.kind, (counts.get(c.kind) ?? 0) + 1);
  const parts: string[] = [];
  const person = counts.get('person') ?? 0;
  const generic = counts.get('generic') ?? 0;
  const external = counts.get('external') ?? 0;
  if (person > 0) parts.push(`${person} ${person === 1 ? 'person' : 'people'}`);
  if (generic > 0) parts.push(`${generic} generic`);
  if (external > 0) parts.push(`${external} external`);
  return parts.join(' · ');
}

/**
 * Merges derived roster pools into the stored pools for a run.
 *
 * Manual rows are never dropped — roster mode adds supply, it does not hide
 * what someone typed in. Both kinds of row sum into the same per-type pool the
 * engine already reads.
 */
export function mergeCapacityRows(
  storedRows: CapacityModelRow[],
  derivedRows: DerivedRosterPool[],
): CapacityModelRow[] {
  if (derivedRows.length === 0) return storedRows;
  return [...storedRows, ...derivedRows];
}

/** True when this scenario's pools should be rolled up from the roster. */
export function scenarioUsesRoster(scenario: Pick<Scenario, 'capacitySource'>): boolean {
  return scenario.capacitySource === 'roster';
}
